import { useCallback } from 'react';
import { 
  getTestId, 
  getLessonStatus, 
  getPassedTestsCount, 
  formatDuration 
} from '../utils/testUtils';

/**
 * Хук для работы с прогрессом уроков
 * @param {Object} userProgress - прогресс пользователя
 * @param {Function} t - функция перевода
 * @returns {Object} объект с методами для работы с прогрессом
 */
export const useLessonProgress = (userProgress, t) => {
  /**
   * Проверяет, завершен ли урок
   * @param {Object} lesson - объект урока
   * @returns {boolean} true если урок завершен
   */
  const isLessonCompleted = useCallback((lesson) => {
    if (!lesson || !userProgress) return false;
    
    const lessonId = getTestId(lesson._id);
    const completedLessonIds = userProgress?.completedLessons || [];
    
    return completedLessonIds.includes(lessonId);
  }, [userProgress]);

  /**
   * Получает статус урока
   * @param {Object} lesson - объект урока
   * @param {Object} module - объект модуля
   * @returns {string} статус урока
   */
  const getItemStatus = useCallback((lesson, module) => {
    const isTest = lesson.isTest || lesson.itemType === 'test';
    
    if (isTest) {
      const testId = getTestId(lesson._id);
      const completedTestIds = userProgress?.completedTests || [];
      const isTestCompleted = completedTestIds.includes(testId);

      if (isTestCompleted) {
        const testResultData = userProgress?.testResults?.[testId];
        if (testResultData) {
          return testResultData.passed ? 'passed' : 'failed';
        } else {
          return 'completed';
        }
      }

      const moduleData = module.module || module;
      if (moduleData.locked || lesson.locked) return 'locked';
      return 'available';
    } else {
      // For lessons
      const moduleData = module.module || module;
      if (moduleData.locked || lesson.locked) return 'locked';

      const isCompleted = isLessonCompleted(lesson);
      return getLessonStatus(lesson, userProgress, isCompleted);
    }
  }, [userProgress, isLessonCompleted]);

  /**
   * Получает информацию о прогрессе тестов урока
   * @param {Object} lesson - объект урока
   * @returns {Object} объект с информацией о прогрессе
   */
  const getTestProgressInfo = useCallback((lesson) => {
    if (!lesson.tests || !Array.isArray(lesson.tests) || lesson.tests.length === 0) {
      return null;
    }

    const passedCount = getPassedTestsCount(lesson.tests, userProgress);
    const totalCount = lesson.tests.length;
    
    return {
      passedCount,
      totalCount,
      displayText: `${passedCount}/${totalCount} ${totalCount === 1 ? t('courses:courseDetail.stats.test') : t('courses:courseDetail.stats.tests')}`
    };
  }, [userProgress, t]);

  /**
   * Получает стили для карточки на основе статуса
   * @param {string} status - статус элемента
   * @param {Object} theme - тема Material-UI
   * @returns {Object} объект стилей
   */
  const getCardStyles = useCallback((status, theme) => {
    const baseStyles = {
      borderRadius: 3,
      border: `1px solid ${theme.palette.divider}`,
      transition: 'all 0.2s ease',
    };

    switch (status) {
      case 'completed':
      case 'passed':
      case 'test_passed':
        return {
          ...baseStyles,
          border: '1px solid',
          borderColor: 'rgba(76, 175, 80, 0.3)',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
        };
      case 'failed':
      case 'test_failed':
        return {
          ...baseStyles,
          border: '1px solid',
          borderColor: 'rgba(244, 67, 54, 0.3)',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
        };
      case 'completed_with_test':
        return {
          ...baseStyles,
          border: '1px solid',
          borderColor: 'rgba(156, 39, 176, 0.3)',
          backgroundColor: 'rgba(156, 39, 176, 0.05)',
        };
      case 'locked':
        return {
          ...baseStyles,
          opacity: 0.6,
          backgroundColor: 'background.paper',
        };
      default:
        return {
          ...baseStyles,
          backgroundColor: 'background.paper',
        };
    }
  }, []);

  /**
   * Получает цвет фона для иконки на основе статуса
   * @param {string} status - статус элемента
   * @returns {string} цвет фона
   */
  const getIconBackgroundColor = useCallback((status) => {
    switch (status) {
      case 'completed':
      case 'test_passed':
        return 'rgba(76, 175, 80, 0.1)';
      case 'test_failed':
        return 'rgba(244, 67, 54, 0.1)';
      case 'completed_with_test':
        return 'rgba(156, 39, 176, 0.1)';
      case 'locked':
        return 'rgba(158, 158, 158, 0.1)';
      default:
        return 'rgba(25, 118, 210, 0.1)';
    }
  }, []);

  /**
   * Получает градиент фона для иконки на основе статуса
   * @param {string} status - статус элемента
   * @returns {string} градиент фона
   */
  const getIconGradient = useCallback((status) => {
    switch (status) {
      case 'completed':
      case 'test_passed':
        return 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.05) 100%)';
      case 'test_failed':
        return 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.05) 100%)';
      case 'completed_with_test':
        return 'linear-gradient(135deg, rgba(156, 39, 176, 0.15) 0%, rgba(156, 39, 176, 0.05) 100%)';
      case 'locked':
        return 'linear-gradient(135deg, rgba(158, 158, 158, 0.15) 0%, rgba(158, 158, 158, 0.05) 100%)';
      default:
        return 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(25, 118, 210, 0.05) 100%)';
    }
  }, []);

  /**
   * Получает цвет иконки на основе статуса
   * @param {string} status - статус элемента
   * @returns {string} цвет иконки
   */
  const getIconColor = useCallback((status) => {
    switch (status) {
      case 'completed':
      case 'test_passed':
        return 'rgba(76, 175, 80, 0.8)';
      case 'test_failed':
        return 'rgba(244, 67, 54, 0.8)';
      case 'completed_with_test':
        return 'rgba(156, 39, 176, 0.8)';
      case 'locked':
        return 'rgba(158, 158, 158, 0.6)';
      default:
        return 'rgba(25, 118, 210, 0.8)';
    }
  }, []);

  return {
    isLessonCompleted,
    getItemStatus,
    getTestProgressInfo,
    getCardStyles,
    getIconBackgroundColor,
    getIconGradient,
    getIconColor,
    formatDuration: (minutes) => formatDuration(minutes, t),
  };
};
